import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { ReactElement, RefAttributes } from 'react';
import { GroupBase } from 'react-select/dist/declarations/src/types';
import type { StateManagerProps } from 'react-select/dist/declarations/src/useStateManager';
import BaseSelect from 'react-select/dist/declarations/src/Select';

type StateManagedSelect<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = (
  props: StateManagerProps<Option, IsMulti, Group> &
    RefAttributes<BaseSelect<Option, IsMulti, Group>>,
) => ReactElement;

type LabelValueOption<T> = {
  value: T;
  label: string;
};

type GroupedOption<T> = {
  label: string;
  options: LabelValueOption<T>[];
};

type SelectOptions<T> = LabelValueOption<T>[] | GroupedOption<T>[];

// Type guard to check if options are grouped
function isGroupedOptions<T>(
  options: SelectOptions<T>,
): options is GroupedOption<T>[] {
  return (
    options.length > 0 &&
    'options' in options[0] &&
    Array.isArray(options[0].options)
  );
}

// Helper to find an option by value in flat or grouped options
function findOptionByValue<T>(
  options: SelectOptions<T>,
  value: T,
): LabelValueOption<T> | undefined {
  if (isGroupedOptions(options)) {
    for (const group of options) {
      const found = group.options.find((opt) => opt.value === value);
      if (found) return found;
    }
    return undefined;
  }
  return options.find((opt) => opt.value === value);
}

// Helper to get the first option value from flat or grouped options
function getFirstOptionValue<T>(options: SelectOptions<T>): T | undefined {
  if (isGroupedOptions(options)) {
    return options[0]?.options[0]?.value;
  }
  return options[0]?.value;
}

/**
 * A specialized version of useSelect that supports different labels and values
 * Now also supports grouped options!
 * @param label The label for the select component
 * @param options Array of options with label and value, or grouped options
 * @param initialSelected Initial value (optional)
 * @param isLoading Loading state flag (optional)
 */
export function useLabelValueSelect<T>(
  label: string,
  options: SelectOptions<T>,
  initialSelected?: T,
  isLoading: boolean = false,
) {
  const defaultSelected =
    initialSelected !== undefined
      ? initialSelected
      : options.length > 0
        ? getFirstOptionValue(options)
        : undefined;

  const [selected, setSelected] = useState<T | undefined>(defaultSelected);

  useEffect(() => {
    if (initialSelected !== undefined) {
      setSelected(initialSelected);
    }
  }, [initialSelected]);

  const SelectT = useMemo(() => {
    return (o: Parameters<StateManagedSelect<LabelValueOption<T>>>[0]) => {
      const defaultOption =
        defaultSelected !== undefined
          ? findOptionByValue(options, defaultSelected)
          : undefined;

      return (
        <div>
          <label className="text-sm font-medium text-broccoli-neutral-700 mb-2">
            {label}
          </label>
          <Select
            classNamePrefix="react-select"
            isLoading={isLoading}
            isMulti={false}
            placeholder={isLoading ? 'Loading...' : 'Select...'}
            defaultValue={defaultOption}
            onChange={(e) => {
              setSelected(e?.value);
            }}
            options={options}
            formatGroupLabel={(group) => (
              <div className="font-semibold text-gray-700">{group.label}</div>
            )}
            {...o}
          />
        </div>
      );
    };
  }, [options, label, defaultSelected, isLoading]);

  return {
    selected,
    SelectT,
  };
}

/**
 * Multi-select version that supports different labels and values
 * Also supports grouped options!
 */
export function useLabelValueMultiSelect<T>(
  label: string,
  options: SelectOptions<T>,
  initialSelected?: T[],
  isLoading: boolean = false,
) {
  const [selected, setSelected] = useState<T[]>(initialSelected || []);

  useEffect(() => {
    if (initialSelected) {
      setSelected(initialSelected);
    }
  }, [initialSelected]);

  const SelectT = useMemo(() => {
    return (
      o: Parameters<StateManagedSelect<LabelValueOption<T>, true>>[0],
    ) => {
      const defaultOptions = initialSelected
        ? initialSelected
            .map((val) => findOptionByValue(options, val))
            .filter((opt): opt is LabelValueOption<T> => opt !== undefined)
        : undefined;

      return (
        <div>
          <label className="text-sm font-medium text-broccoli-neutral-700 mb-2">
            {label}
          </label>
          <Select
            isLoading={isLoading}
            isMulti={true}
            placeholder={isLoading ? 'Loading...' : 'Select...'}
            defaultValue={defaultOptions}
            onChange={(e) => {
              setSelected(Array.from(e?.values()).map((v) => v.value) ?? []);
            }}
            options={options}
            formatGroupLabel={(group) => (
              <div className="font-semibold text-gray-700">{group.label}</div>
            )}
            {...o}
          />
        </div>
      );
    };
  }, [options, label, initialSelected, isLoading]);

  return {
    selected,
    SelectT,
  };
}
